import { TitleCasePipe, KeyValuePipe } from '@angular/common';
import { Component, Inject, Input, Optional, inject, OnDestroy } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatChipsModule } from '@angular/material/chips';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { Bundle, Organization } from 'fhir/r5';
import { Subscription } from 'rxjs';
import { DetailsCardzComponent } from '../details-cardz/details-cardz.component';
import { SpecialHeaderComponent } from '../shared/special-header/special-header.component';
import { LinkInReferencesService } from '../shared/link-in-references.service';
import { UtilityService } from '../shared/utility.service';
import { AuthService, capacityObject } from '../shared/auth/auth.service';
import { EncounterServiceService } from '../patient-wrapper/encounter-service.service';
import { HospitalService } from './hospital.service';
import { ErrorService } from '../shared/error.service';
import { backendEndPointToken } from '../app.config';

@Component({
  selector: 'app-hospital-details',
  standalone: true,
  imports: [
    MatCardModule,
    MatIconModule,
    MatDividerModule,
    MatBadgeModule,
    MatChipsModule,
    TitleCasePipe,
    KeyValuePipe,
    SpecialHeaderComponent,
    DetailsCardzComponent
  ],
  templateUrl: './hospital-details.component.html',
  styleUrls: ['./hospital-details.component.scss']
})
export class HospitalDetailsComponent implements OnDestroy {
  linkInReferences = inject(LinkInReferencesService);
  private backendUrl = inject(backendEndPointToken);

  http = inject(HttpClient);
  utilityService = inject(UtilityService);
  route = inject(ActivatedRoute);
  auth = inject(AuthService);
  encounterService = inject(EncounterServiceService);
  hosServ = inject(HospitalService);
  snackBar = inject(MatSnackBar);
  errorService = inject(ErrorService);

  realHospitalDetails?: any;
  @Input() hospitalDetailsFromInput?: Bundle;
  patientId: string | null = null;
  user: any = null;
  capacityObject = capacityObject;
  parentOrganizationId: string | null = null;
  parentOrganizationName: string | null = null;

  // Subscription management
  private subscriptions = new Subscription();

  detailsBuilderObject: any = {
    resourceName: 'Hospital',
    resourceIcon: 'local_hospital',
    specialHeader: {
      specialHeaderKey: 'name',
      specialHeaderIcon: 'local_hospital',
      specialHeaderDataType: 'string',
      ReferenceDeepPath: null,
      valueDeepPath: null,
    },
    groups: [
      {
        groupName: 'Details',
        groupIcon: 'info',
        groupMembers: [],
      },
      {
        groupName: 'Phone',
        groupIcon: 'phone',
        groupMembers: []
      },
      {
        groupName: 'Email',
        groupIcon: 'email',
        groupMembers: []
      },
      {
        groupName: 'Other Contacts',
        groupIcon: 'contacts',
        groupMembers: []
      },
      {
        groupName: 'Addresses',
        groupIcon: 'location_on',
        grouped: true,
        groupMembers: []
      },
      {
        groupName: 'Qualifications',
        groupIcon: 'verified',
        groupMembers: []
      },
    ],
  };

  hospitalDetailsFromDialog: any = (() => { 
    try { 
      return inject(MAT_DIALOG_DATA); 
    } catch { 
      return null; 
    } 
  })();

  ngOnInit() {
    // Subscribe to user authentication
    const userSub = this.auth.user.subscribe((user) => {
      this.user = user;
      if (this.route.parent?.routeConfig?.path?.includes('testing')) {
        this.encounterService.setEncounterState('100001', 'in-progress');
        this.user = { role: 'testing' };
        this.patientId = '100001';
        this.capacityObject['labRequest']['request'].push('testing');
      }

      this.loadOrganizationData();
    });

    this.subscriptions.add(userSub);

    // Subscribe to organization updates from HospitalService
    const updateSub = this.hosServ.onOrganizationUpdated.subscribe({
      next: (updatedOrg) => {
        console.log('🔔 Received organization update notification:', updatedOrg);
        this.handleOrganizationUpdate(updatedOrg);
      },
      error: (err) => {
        console.error('❌ Error in organization update subscription:', err);
      }
    });

    this.subscriptions.add(updateSub);
  }

  ngOnDestroy() {
    // Clean up all subscriptions
    this.subscriptions.unsubscribe();
  }

  /**
   * Handle organization update from service
   * @param updatedOrg - Fresh organization data from backend
   */
  private handleOrganizationUpdate(updatedOrg: any): void {
    console.log('🔄 Handling organization update...');

    // Update the component's data
    this.realHospitalDetails = updatedOrg;

    // Reset and reprocess the details builder object
    this.resetDetailsBuilder();
    this.processOrganizationDetails();

    // Show success message
    this.snackBar.open('✨ Display refreshed with latest data', 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['success-snackbar']
    });

    console.log('✅ Organization display updated successfully');
  }

  /**
   * Reset details builder to initial state
   */
  private resetDetailsBuilder(): void {
    this.detailsBuilderObject.groups.forEach((group: any) => {
      group.groupMembers = [];
    });

    // Remove any dynamically added groups (like qualifications, licenses)
    this.detailsBuilderObject.groups = this.detailsBuilderObject.groups.filter((group: any) => {
      return ['Details', 'Phone', 'Email', 'Other Contacts', 'Addresses', 'Qualifications'].includes(group.groupName);
    });
  }

  private loadOrganizationData(): void {
    if (!this.user) {
      this.snackBar.open('User not authenticated', 'Close', { duration: 4000 });
      return;
    }

    const userOrgId = this.user.organizationId;

    if (!userOrgId) {
      this.snackBar.open('No organization associated with user', 'Close', { duration: 4000 });
      return;
    }

    this.loadOrganizationById(userOrgId);
  }

  private loadOrganizationById(orgId: string): void {
    const orgUrl = `${this.backendUrl}/Organization/${orgId}`;

    console.log(`📋 Fetching organization: ${orgId}`);

    this.http.get<Organization>(orgUrl, { 
      headers: { 'Accept': 'application/fhir+json' } 
    }).subscribe({
      next: (org: Organization) => {
        console.log(`📋 Organization loaded:`, org);

        this.realHospitalDetails = this.hospitalDetailsFromInput || this.hospitalDetailsFromDialog || org;
        this.processOrganizationDetails();
      },
      error: (err) => {
        console.error(`Failed to load Organization ${orgId}:`, err);
        this.errorService.openandCloseError('Failed to load organization details');
        this.snackBar.open('Error loading organization details', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error']
        });
      }
    });
  }

  onEditOrganization(): void {
    if (!this.realHospitalDetails) {
      this.snackBar.open('No organization data to edit', 'Close', { duration: 4000 });
      return;
    }

    console.log('✏️ Editing organization:', this.realHospitalDetails);
    this.hosServ.editOrganization(this.realHospitalDetails);
  }

  /**
   * Extract parent organization from extensions
   */
  private extractParentOrganization(): void {
    if (!this.realHospitalDetails?.extension) {
      return;
    }

    const extArray = this.realHospitalDetails.extension;

    for (const ext of extArray) {
      if (ext.extension && Array.isArray(ext.extension)) {
        for (const nestedExt of ext.extension) {
          if (nestedExt.url === 'parentOrganization' && nestedExt.valueReference) {
            const refParts = nestedExt.valueReference.reference?.split('/') || [];
            this.parentOrganizationId = refParts[refParts.length - 1] || null;
          } else if (nestedExt.url === 'parentOrganizationName' && nestedExt.valueString) {
            this.parentOrganizationName = nestedExt.valueString;
          }
        }
      }
    }
  }

  /**
   * Process all extension data
   */
  private processExtensionData(): void {
    if (!this.realHospitalDetails?.extension) {
      return;
    }

    const extArray = this.realHospitalDetails.extension;
    const detailsGroup = this.detailsBuilderObject.groups.find((g: any) => g.groupName === 'Details');

    if (!detailsGroup) return;

    // Process simple extensions
    for (const ext of extArray) {
      if (ext.url === 'http://elikita.org/fhir/StructureDefinition/organization-description' && ext.valueString) {
        detailsGroup.groupMembers.push({
          key: 'description',
          label: 'Description',
          keyDataType: 'string',
          referenceDeepPath: null,
          valueDeepPath: null,
          staticValue: ext.valueString
        });
      }
    }

    // Process nested extensions
    for (let extIdx = 0; extIdx < extArray.length; extIdx++) {
      const ext = extArray[extIdx];

      if (!ext.extension || !Array.isArray(ext.extension) || ext.extension.length === 0) {
        continue;
      }

      let groupName = 'Other Information';
      const urlParts = ext.url?.split('/') || [];
      const urlLastPart = urlParts[urlParts.length - 1] || '';

      if (urlLastPart.includes('qualification')) {
        groupName = 'Qualifications';
      } else if (urlLastPart.includes('license')) {
        groupName = 'Licenses';
      } else if (urlLastPart.includes('certificate')) {
        groupName = 'Certificates';
      } else if (urlLastPart.includes('accreditation')) {
        groupName = 'Accreditations';
      }

      let targetGroup = this.detailsBuilderObject.groups.find((g: any) => g.groupName === groupName);

      if (!targetGroup) {
        targetGroup = {
          groupName: groupName,
          groupIcon: this.getIconForGroup(groupName),
          groupMembers: []
        };
        this.detailsBuilderObject.groups.push(targetGroup);
      }

      const nestedData: any = {};
      let hasValidData = false;

      for (const nestedExt of ext.extension) {
        const nestedUrl = nestedExt.url || 'unknown';
        const value = nestedExt.valueString || nestedExt.valueUri || nestedExt.valueReference?.reference || '';

        if (value) {
          hasValidData = true;
          nestedData[nestedUrl] = value;
        }
      }

      if (hasValidData) {
        const label = `${groupName} ${(targetGroup.groupMembers?.length || 0) + 1}`;

        targetGroup.groupMembers.push({
          key: 'nestedExtension',
          label: label,
          keyDataType: 'object',
          referenceDeepPath: null,
          valueDeepPath: null,
          staticValue: nestedData,
          isNestedExtension: true
        });
      }
    }
  }

  private getIconForGroup(groupName: string): string {
    const iconMap: { [key: string]: string } = {
      'Qualifications': 'school',
      'Licenses': 'card_membership',
      'Certificates': 'verified_user',
      'Accreditations': 'workspaces_filled',
      'Other Information': 'info'
    };
    return iconMap[groupName] || 'info';
  }

  /**
   * Main processing method
   */
  private processOrganizationDetails(): void {
    if (!this.realHospitalDetails) {
      this.snackBar.open('No organization resource found', 'Close', { duration: 4000 });
      return;
    }

    this.extractParentOrganization();

    const detailsGroup = this.detailsBuilderObject.groups.find((g: any) => g.groupName === 'Details');
    if (detailsGroup) {
      detailsGroup.groupMembers = [];

      detailsGroup.groupMembers.push({
        key: 'name',
        label: 'Name',
        keyDataType: 'string',
        referenceDeepPath: null,
        valueDeepPath: null
      });

      if (this.realHospitalDetails.type?.[0]) {
        detailsGroup.groupMembers.push({
          key: 'type',
          label: 'Type',
          keyDataType: 'CodeableConcept',
          referenceDeepPath: null,
          valueDeepPath: ['0'].join('$#$')
        });
      }

      if (this.parentOrganizationId) {
        detailsGroup.groupMembers.push({
          key: 'parentOrganization',
          label: 'Parent Organization',
          keyDataType: 'badge',
          referenceDeepPath: null,
          valueDeepPath: null,
          staticValue: this.parentOrganizationName || this.parentOrganizationId,
          badgeActive: true
        });
      }
    }

    this.processExtensionData();
    this.populateAddresses();
    this.populateTelecom();
    this.populateContacts();
  }

  private populateAddresses(): void {
    const addrGroup = this.detailsBuilderObject.groups.find((g: any) => g.groupName === 'Addresses');
    if (!addrGroup) return;

    addrGroup.groupMembers = [];
    const addresses = ((this.realHospitalDetails as any)['address'] as any[]) ?? [];

    if (addresses.length > 0) {
      addresses.forEach((addr: any, idx: number) => {
        const base = `Address ${idx + 1}`;

        const addressParts = [
          addr.line?.join(', '),
          addr.city,
          addr.state,
          addr.postalCode,
          addr.country
        ].filter(Boolean);

        const addressSummary = addressParts.join(', ');

        if (addressSummary) {
          addrGroup.groupMembers.push({
            key: 'address',
            label: base,
            keyDataType: 'string',
            referenceDeepPath: null,
            valueDeepPath: [String(idx), 'text'].join('$#$'),
            staticValue: addressSummary
          });
        }

        if (addr.line?.length > 0) {
          addrGroup.groupMembers.push({
            key: 'address',
            label: `${base} - Street`,
            keyDataType: 'string',
            referenceDeepPath: null,
            valueDeepPath: [String(idx), 'line'].join('$#$')
          });
        }

        if (addr.city) {
          addrGroup.groupMembers.push({
            key: 'address',
            label: `${base} - City`,
            keyDataType: 'string',
            referenceDeepPath: null,
            valueDeepPath: [String(idx), 'city'].join('$#$')
          });
        }

        if (addr.state) {
          addrGroup.groupMembers.push({
            key: 'address',
            label: `${base} - State`,
            keyDataType: 'string',
            referenceDeepPath: null,
            valueDeepPath: [String(idx), 'state'].join('$#$')
          });
        }

        if (addr.postalCode) {
          addrGroup.groupMembers.push({
            key: 'address',
            label: `${base} - Postal Code`,
            keyDataType: 'string',
            referenceDeepPath: null,
            valueDeepPath: [String(idx), 'postalCode'].join('$#$')
          });
        }

        if (addr.country) {
          addrGroup.groupMembers.push({
            key: 'address',
            label: `${base} - Country`,
            keyDataType: 'string',
            referenceDeepPath: null,
            valueDeepPath: [String(idx), 'country'].join('$#$')
          });
        }
      });
    }
  }

  private populateTelecom(): void {
    const phoneGroup = this.detailsBuilderObject.groups.find((g: any) => g.groupName === 'Phone');
    const emailGroup = this.detailsBuilderObject.groups.find((g: any) => g.groupName === 'Email');
    const otherGroup = this.detailsBuilderObject.groups.find((g: any) => g.groupName === 'Other Contacts');

    if (!((this.realHospitalDetails as any)['telecom']?.length)) return;

    if (phoneGroup) {
      phoneGroup.groupMembers = [];
      let phoneCounter = 0;
      ((this.realHospitalDetails as any)['telecom'] as any[]).forEach((tele: any, idx: number) => {
        const sys = (tele.system || '').toString().toLowerCase();
        if (sys === 'phone') {
          phoneCounter += 1;
          phoneGroup.groupMembers.push({
            key: 'telecom',
            label: `Phone ${phoneCounter}`,
            keyDataType: 'phone',
            referenceDeepPath: null,
            valueDeepPath: [String(idx), 'value'].join('$#$'),
          });
        }
      });
    }

    if (emailGroup) {
      emailGroup.groupMembers = [];
      let emailCounter = 0;
      ((this.realHospitalDetails as any)['telecom'] as any[]).forEach((tele: any, idx: number) => {
        const sys = (tele.system || '').toString().toLowerCase();
        if (sys === 'email') {
          emailCounter += 1;
          emailGroup.groupMembers.push({
            key: 'telecom',
            label: `Email ${emailCounter}`,
            keyDataType: 'email',
            referenceDeepPath: null,
            valueDeepPath: [String(idx), 'value'].join('$#$'),
          });
        }
      });
    }

    if (otherGroup) {
      otherGroup.groupMembers = [];
      ((this.realHospitalDetails as any)['telecom'] as any[]).forEach((tele: any, idx: number) => {
        const rawSys = tele.system || 'other';
        const sysLower = rawSys.toString().toLowerCase();
        if (sysLower !== 'phone' && sysLower !== 'email') {
          otherGroup.groupMembers.push({
            key: 'telecom',
            label: rawSys,
            keyDataType: 'string',
            referenceDeepPath: null,
            valueDeepPath: [String(idx), 'value'].join('$#$'),
          });
        }
      });
    }
  }

  private populateContacts(): void {
    const contacts = this.realHospitalDetails.contact ?? [];
    if (!Array.isArray(contacts) || !contacts.length) return;

    contacts.forEach((ct: any, cIdx: number) => {
      const contactGroupDyn: any = {
        groupName: `Contact ${cIdx + 1}`,
        groupIcon: 'person',
        groupMembers: [] as any[]
      };

      if (ct?.purpose) {
        contactGroupDyn.groupMembers.push({
          key: 'contact',
          label: 'purpose',
          keyDataType: 'CodeableConcept',
          referenceDeepPath: null,
          valueDeepPath: [String(cIdx), 'purpose', 'text'].join('$#$'),
        });
      }

      if (ct?.name) {
        contactGroupDyn.groupMembers.push({
          key: 'contact',
          label: 'name',
          keyDataType: 'string',
          referenceDeepPath: null,
          valueDeepPath: [String(cIdx), 'name', 'text'].join('$#$'),
        });
      }

      if (Array.isArray(ct?.telecom) && ct.telecom.length) {
        let phoneN = 0;
        let emailN = 0;
        ct.telecom.forEach((tp: any, tIdx: number) => {
          const sys = (tp?.system || '').toString().toLowerCase();
          if (sys === 'phone') {
            phoneN += 1;
            contactGroupDyn.groupMembers.push({
              key: 'contact',
              label: `telecom phone ${phoneN}`,
              keyDataType: 'phone',
              referenceDeepPath: null,
              valueDeepPath: [String(cIdx), 'telecom', String(tIdx), 'value'].join('$#$'),
            });
          } else if (sys === 'email') {
            emailN += 1;
            contactGroupDyn.groupMembers.push({
              key: 'contact',
              label: `telecom email ${emailN}`,
              keyDataType: 'email',
              referenceDeepPath: null,
              valueDeepPath: [String(cIdx), 'telecom', String(tIdx), 'value'].join('$#$'),
            });
          }
        });
      }

      if (contactGroupDyn.groupMembers.length) {
        this.detailsBuilderObject.groups.push(contactGroupDyn);
      }
    });
  }
}